import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton as CloseIconButton
} from '@mui/material';
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon
} from '@mui/icons-material';
import { Carousel } from 'react-responsive-carousel';
import { openWhatsAppChatForProperty } from '../util/whatsappUtils';
import { useFavorite } from '../util/useFavorite';
import CropFreeOutlinedIcon from '@mui/icons-material/CropFreeOutlined';
import { PropertyDetailsCard } from './section-components/PropertyDetailsCard';

const EnhancedPropertyCard = ({ property, disableNavigation = false, viewMode = 'grid', propertyType: passedPropertyType }) => {
  const navigate = useNavigate();
  const [openPropertyDialog, setOpenPropertyDialog] = useState(false);
  const [showCallConfirmation, setShowCallConfirmation] = useState(false);

  // Common styles
  const chipStyle = {
    fontWeight: 'bold',
    fontSize: { xs: '0.6rem', sm: '0.7rem' },
    borderRadius: '4px',
    height: { xs: 20, sm: 24 },
    '& .MuiChip-label': { px: { xs: 0.5, sm: 1 } }
  };

  const isAgriculturalProperty = () => {
    const explicitType = (passedPropertyType || property?.propertyType || property?.type || property?.propertyCategory)?.toLowerCase();
    const areaUnit = property?.area?.unit?.toLowerCase();

    return explicitType === 'agricultural' || areaUnit === 'hectares' || areaUnit === 'acres';
  };

  // Fallback values if property data is missing
  const {
    images = [],
    title,
    name,
    address,
    location,
    propertyType,
    type,
    area,
    landSize,
    facing = 'West',
    propertyStatus = 'For Sale',
    listingType,
    price,
    rentOrPrice,
    id,
    _id
  } = property || {};

  // Use new API fields with fallbacks to old field names for backward compatibility
  const displayTitle = title || name || 'Property Name';
  const displayLocation = address || location || 'Location';
  const displayType = passedPropertyType || property?.propertyType || property?.type || 'RESIDENTIAL PLOT';

  // Format area value
  const formatAreaValue = (val) => {
    if (!val && val !== 0) return '';
    if (typeof val === 'object') {
      const { value, unit = '' } = val;
      return value ? `${value} ${unit}`.trim() : '';
    }
    const num = Number(val);
    return Number.isNaN(num) ? String(val) : `${num} Sq Ft`;
  };

  const displayArea = formatAreaValue(area) || formatAreaValue(landSize) || '';
  const displayPrice = price || rentOrPrice || '₹ Contact for Price';

  // Normalize display status: prioritize propertyStatus, fall back to listingType
  const getDisplayStatus = () => {
    const ps = (propertyStatus || '').toLowerCase().trim();
    const lt = (listingType || '').toLowerCase().trim();

    // Direct matches for propertyStatus
    if (ps === 'for rent') return 'For Rent';
    if (ps === 'rented') return 'Rented';
    if (ps === 'sold') return 'Sold';
    if (ps === 'for sale') return 'For Sale';
    if (ps === 'available') return 'Available';

    // Fallback to listingType
    if (lt === 'rent' || lt === 'to let' || lt === 'for rent' || lt === 'rented') {
      return lt.includes('rented') ? 'Rented' : 'For Rent';
    }
    if (lt === 'sale' || lt === 'for sale' || lt === 'sell') return 'For Sale';

    // If propertyStatus has a value but didn't match anything above, return it capitalized
    if (ps) return ps.charAt(0).toUpperCase() + ps.slice(1);

    // Default
    return 'For Sale';
  };

  const displayStatus = getDisplayStatus();

  // Featured detection
  const isFeatured = Boolean(
    property?.featured || property?.isFeatured || property?.featuredProperty || property?.is_featured
  );
  const isStatusActive = (property?.status || '').toLowerCase() === 'active';

  // Determine if property should show as featured
  const category = (property?.propertyCategory || '').toLowerCase();
  const typeLower = (displayType || '').toLowerCase();
  const isValidCategory = ['residential', 'commercial', 'agricultural'].some(cat =>
    typeLower.includes(cat) || category.includes(cat)
  );
  const isFeaturedType = isFeatured && isStatusActive && isValidCategory;

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹ Contact for Price';

    // If it's already a string with text (alphanumeric), return as is or prepend ₹ if needed
    if (typeof amount === 'string' && isNaN(amount)) {
      if (amount.includes('₹') || amount.includes('£')) return amount;
      return `₹ ${amount}`;
    }

    const num = Number(amount);
    if (isNaN(num)) return amount;

    const isUK = property?.country === 'United Kingdom';
    return new Intl.NumberFormat(isUK ? 'en-GB' : 'en-IN', {
      style: 'currency',
      currency: isUK ? 'GBP' : 'INR',
      minimumFractionDigits: 0,
    }).format(num);
  };

  // Use either id or _id for navigation (removed unused variable)

  const contactNumber = property?.contactNumber || property?.phone || property?.phoneNumber || property?.agentPhone || property?.ownerPhone || '+917893092211';
  const handleCall = (e) => {
    e.stopPropagation();
    setShowCallConfirmation(true);
  };

  const handleConfirmCall = () => {
    // Make a direct WhatsApp call or phone call based on preference
    // Given the commercial component uses WhatsApp call url:
    const phoneNumber = '917893092211';
    const whatsappCallUrl = `https://wa.me/${phoneNumber}?call=1`;
    window.open(whatsappCallUrl, '_blank');
    setShowCallConfirmation(false);
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation(); // Prevent card click when clicking WhatsApp button
    openWhatsAppChatForProperty(property);
  };

  const handlePropertyDetailsOpen = (e) => {
    e.stopPropagation(); // Prevent card click when clicking property details button
    setOpenPropertyDialog(true);
  };

  const handlePropertyDetailsClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenPropertyDialog(false);
    // Do not navigate anywhere - just close the dialog
  };

  const handleViewMore = () => {
    setOpenPropertyDialog(false);
    const possibleId = property?._id || property?.id || property?.propertyId || property?.ID;
    if (possibleId) {
      navigate(`/property/${possibleId}`);
    } else {
      alert(`Property ID missing. Available fields: ${Object.keys(property || {}).join(', ')}`);
    }
  };

  const handleCardClick = (e) => {
    if (disableNavigation) return;
    setOpenPropertyDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'for sale':
      case 'active':
      case 'available':
        return '#666';
      case 'for rent':
        return '#2196f3'; // Blue for rent
      case 'rented':
        return '#f44336'; // Red for rented (similar to sold)
      case 'urgent sale':
        return '#4caf50';
      case 'sold':
        return '#f44336';
      case 'pending':
        return '#ff9800';
      case 'inactive':
      case 'draft':
        return '#9e9e9e';
      default:
        return '#666';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'residential plot':
      case 'residential':
        return '#ffc107';
      case 'commercial plot':
      case 'commercial':
        return '#2196f3';
      case 'agricultural':
        return '#4caf50';
      default:
        return '#ffc107';
    }
  };

  // Get property ID for favorites
  const propertyId = property?._id || property?.id || property?.propertyId || property?.ID;

  // Use custom favorite hook
  const { isFavorite, handleFavoriteToggle } = useFavorite(propertyId);

  const handleFavoriteClick = (e) => {
    e.stopPropagation(); // prevents parent click
    handleFavoriteToggle(e);
  };


  const [showViewMore, setShowViewMore] = React.useState(false);

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <Card
        onClick={disableNavigation ? undefined : handleCardClick}
        sx={{
          width: '100%',
          maxWidth: viewMode === 'list' ? '100%' : { xs: '100%', sm: 380, md: 400 },
          margin: viewMode === 'list' ? 0 : { xs: '8px auto', sm: 2 },
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          position: 'relative',
          cursor: disableNavigation ? 'default' : 'pointer',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          userSelect: 'none',
          '&:hover': {
            transform: { xs: 'none', sm: 'translateY(-4px)' },
            boxShadow: { xs: '0 4px 12px rgba(0,0,0,0.1)', sm: '0 8px 24px rgba(0,0,0,0.15)' },
            backgroundColor: { xs: 'transparent', sm: 'rgba(248, 249, 250, 0.8)' },
            '& .MuiCardContent-root': {
              backgroundColor: { xs: 'transparent', sm: 'rgba(248, 249, 250, 0.5)' }
            }
          },
          '&:active': {
            transform: { xs: 'scale(0.98)', sm: 'translateY(-2px)' },
            backgroundColor: { xs: 'rgba(240, 240, 240, 0.5)', sm: 'rgba(240, 240, 240, 0.9)' },
          }
        }}
      >
        {/* Property Image with Tags */}
        <Box sx={{ position: 'relative' }}>
          <Carousel
            showThumbs={false}
            infiniteLoop={images?.length > 1}
            showStatus={false}
            autoPlay={images?.length > 1}
            interval={3500}
            transitionTime={600}
            stopOnHover={true}
            emulateTouch
            swipeable
            showArrows={images?.length > 1}
            showIndicators={images?.length > 1}
            renderArrowPrev={(onClickHandler, hasPrev, label) => hasPrev && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onClickHandler(); }}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }
                }}
                aria-label={label}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </IconButton>
            )}
            renderArrowNext={(onClickHandler, hasNext, label) => hasNext && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onClickHandler(); }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }
                }}
                aria-label={label}
              >
                <ArrowForwardIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </IconButton>
            )}
            renderIndicator={(onClickHandler, isSelected, index, label) => (
              <button
                key={index}
                type="button"
                onClick={(e) => { e.stopPropagation(); onClickHandler(); }}
                aria-label={`${label} ${index + 1}`}
                style={{
                  border: 'none',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  margin: '0 4px',
                  background: isSelected ? '#d8b035' : 'rgba(255,255,255,0.6)',
                  padding: 0
                }}
              />
            )}
            swipeScrollTolerance={5}
          >
            {images && images.length ? (
              images.map((img, i) => (
                <div key={i} style={{ height: 300, overflow: 'hidden' }}>
                  <img
                    src={img || 'https://via.placeholder.com/400x300/f0f0f0/666666?text=Property+Image'}
                    alt={`${displayTitle} ${i + 1}`}
                    style={{ width: '100%', height: 300, objectFit: 'cover', cursor: disableNavigation ? 'default' : 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disableNavigation) return;
                      handleCardClick(e);
                    }}
                  />
                </div>
              ))
            ) : (
              <div style={{ height: 300, overflow: 'hidden' }}>
                <img
                  src="https://via.placeholder.com/400x300/f0f0f0/666666?text=Property+Image"
                  alt={displayTitle}
                  style={{ width: '100%', height: 300, objectFit: 'cover' }}
                />
              </div>
            )}
          </Carousel>

          {/* Property Status Badge (left) */}
          <Box sx={{ position: 'absolute', top: { xs: 4, sm: 8 }, left: { xs: 4, sm: 8 }, zIndex: 11 }}>
            <Chip
              label={displayStatus?.toUpperCase() || 'FOR SALE'}
              size="small"
              variant="filled"
              sx={{
                ...chipStyle,
                backgroundColor: getStatusColor(displayStatus),
                color: 'white'
              }}
            />
          </Box>

          {/* Featured Badge (left) */}
          {/* {isFeaturedType && (
          <Box sx={{ position: 'absolute', top: { xs: 4, sm: 8 }, left: { xs: 4, sm: 8 }, zIndex: 11 }}>
            <Chip
              label="FEATURED"
              size="small"
              variant="filled"
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                borderRadius: '4px',
                height: { xs: 20, sm: 24 },
                '& .MuiChip-label': {
                  px: { xs: 0.5, sm: 1 }
                }
              }}
            />
          </Box>
        )} */}

          {/* Status Tags (right) */}
          <Box sx={{
            position: 'absolute',
            top: { xs: 4, sm: 8 },
            right: { xs: 4, sm: 8 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.3, sm: 0.5 }
          }}>
            {isFeaturedType && (
              <Chip
                label="FEATURED"
                size="small"
                variant="filled"
                sx={{
                  ...chipStyle,
                  backgroundColor: '#4caf50',
                  color: 'white'
                }}
              />
            )}
            {displayStatus?.toLowerCase().includes('urgent') && (
              <Chip
                label="URGENT SALE"
                size="small"
                sx={{
                  ...chipStyle,
                  backgroundColor: '#4caf50',
                  color: 'white'
                }}
              />
            )}
          </Box>

          {/* Action Icons */}
          <Box sx={{
            position: 'absolute',
            bottom: { xs: 4, sm: 8 },
            right: { xs: 4, sm: 8 },
            display: 'flex',
            gap: { xs: 0.3, sm: 0.5 }
          }}>
            <IconButton
              size="small"
              onClick={handlePropertyDetailsOpen}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
              }}
            >
              <CropFreeOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleFavoriteClick}
              sx={{
                backgroundColor: "rgba(0,0,0,0.6)",
                color: isFavorite ? "red" : "white",
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.8)",
                  color: "red",
                },
                transition: "color 0.3s ease, transform 0.2s",
                "&:active": { transform: "scale(1.2)" }
              }}
            >
              <FavoriteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
            </IconButton>
          </Box>
        </Box>

        <CardContent sx={{
          padding: { xs: 1.5, sm: 2 },
          '&:last-child': { paddingBottom: { xs: 1.5, sm: 2 } }
        }}>
          {/* Property Type Badge */}
          <Chip
            label={displayType?.toUpperCase() || 'RESIDENTIAL PLOT'}
            sx={{
              backgroundColor: getTypeColor(displayType),
              // color: type?.toLowerCase().includes('commercial') ? 'white' : '#000',
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              marginBottom: { xs: 0.8, sm: 1 },
              borderRadius: '4px',
              height: { xs: 22, sm: 24 },
              '& .MuiChip-label': {
                px: { xs: 0.8, sm: 1 }
              }
            }}
          />

          {/* Call to Action */}
          <Typography
            variant="subtitle2"
            sx={{
              color: '#d8b035',
              fontWeight: 'bold',
              marginBottom: { xs: 0.8, sm: 1 },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              lineHeight: 1.2
            }}
          >
            For the Latest Rates - Call Now!
            <PhoneIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#d32f2f' }} />
          </Typography>

          {/* Debug Navigation Buttons */}
          {/* <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button 
            size="small" 
            variant="contained" 
            onClick={handleCardClick}
            sx={{ 
              backgroundColor: '#ff9800',
              fontSize: '0.7rem',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            🔗 Card Click (ID: {propertyId || property?._id || property?.propertyId || 'MISSING'})
          </Button>
          <Button 
            size="small" 
            variant="contained" 
            onClick={(e) => {
              e.stopPropagation();
              console.log('🧪 Direct navigation test to hardcoded ID');
              navigate('/property/67cb14a2911a42b67d17744b');
            }}
            sx={{ 
              backgroundColor: '#4caf50',
              fontSize: '0.7rem',
              '&:hover': { backgroundColor: '#388e3c' }
            }}
          >
            🧪 Test Real ID
          </Button>
        </Box> */}

          {/* Property Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              marginBottom: { xs: 0.8, sm: 1 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#333'
            }}
          >
            {displayTitle}
          </Typography>

          {/* Location */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: { xs: 1, sm: 2 },
            color: '#666'
          }}>
            <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, marginRight: 0.5 }} />
            <Typography variant="body2" sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {displayLocation}
            </Typography>
          </Box>


          {/* Property Details */}
          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            marginBottom: { xs: 1.5, sm: 2 },
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Plot price/value */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <Box sx={{
                width: { xs: 16, sm: 18 },
                height: { xs: 16, sm: 18 },
                backgroundColor: '#d8b035',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="caption" sx={{
                  color: 'white',
                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  fontWeight: 'bold'
                }}>
                  ₹
                </Typography>
              </Box>
              <Typography variant="body2" sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: '#d8b035'
              }}>
                {formatCurrency(displayPrice)}
              </Typography>
            </Box>

            {/* Facing - Hide for agricultural properties */}
            {!isAgriculturalProperty() && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 'fit-content'
              }}>
                <Box sx={{
                  width: { xs: 14, sm: 16 },
                  height: { xs: 14, sm: 16 },
                  backgroundColor: '#d8b035',
                  borderRadius: '50%'
                }} />
                <Typography variant="body2" sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  {facing || 'West'}
                </Typography>
              </Box>
            )}

            {/* Area */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <Box sx={{
                width: { xs: 14, sm: 16 },
                height: { xs: 14, sm: 16 },
                backgroundColor: '#d8b035',
                borderRadius: 1
              }} />
              <Typography variant="body2" sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}>
                {displayArea}
              </Typography>
            </Box>
          </Box>


          {/* Action Buttons */}
          <Grid container spacing={{ xs: 1, sm: 1 }} sx={{ mt: 'auto' }}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PhoneIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                onClick={handleCall}
                sx={{
                  borderColor: '#d8b035',
                  color: '#d8b035',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  padding: { xs: '8px', sm: '8px 16px' },
                  minHeight: { xs: 40, sm: 40 },
                  '&:hover': {
                    borderColor: '#d8b035',
                    backgroundColor: '#d8b035',
                    color: '#ffffff',
                    transition: 'all 0.3s ease',
                  }
                }}
              >
                Call
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<WhatsAppIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                onClick={handleWhatsApp}
                sx={{
                  borderColor: '#d8b035',
                  color: '#d8b035',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  padding: { xs: '8px', sm: '8px 16px' },
                  minHeight: { xs: 40, sm: 40 },
                  '&:hover': {
                    borderColor: '#d8b035',
                    backgroundColor: '#d8b035',
                    color: '#ffffff',
                    transition: 'all 0.3s ease',
                  }
                }}
              >
                WhatsApp
              </Button>
            </Grid>
          </Grid>

        </CardContent>

        {/* Property Details Popup Dialog */}
        <Dialog
          open={openPropertyDialog}
          onClose={handlePropertyDetailsClose}
          maxWidth="lg"
          fullWidth
          fullScreen={{ xs: true, sm: false }}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 0, sm: '8px' },
              maxHeight: { xs: '100vh', sm: '90vh' },
              width: { xs: '100vw', sm: '95vw' },
              maxWidth: { xs: 'none', sm: '1200px' },
              margin: { xs: 0, sm: 'auto' }
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0',
              padding: { xs: '12px 16px', sm: '16px 24px' },
              backgroundColor: '#f8f9fa'
            }}
          >
            <Typography variant="h6" sx={{
              fontWeight: 'bold',
              color: '#333',
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
              Property Details
            </Typography>
            <CloseIconButton
              onClick={(e) => handlePropertyDetailsClose(e)}
              sx={{
                color: '#666',
                padding: { xs: '4px', sm: '8px' },
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </CloseIconButton>
          </DialogTitle>
          <DialogContent sx={{
            padding: 0,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: { xs: '4px', sm: '6px' }
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px'
            }
          }}
            onScroll={(e) => {
              if (showViewMore) return; // prevent re-check

              const { scrollTop, scrollHeight, clientHeight } = e.target;
              const reachedBottom = scrollTop + clientHeight >= scrollHeight - 10;

              if (reachedBottom) {
                setShowViewMore(true);
              }
            }}
          >
            <PropertyDetailsCard
              property={property}
              propertyType={passedPropertyType}
              hideRightSidebar={true}
            />

          </DialogContent>
          <DialogActions
            sx={{
              padding: { xs: '12px 16px', sm: '16px 24px' },
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              justifyContent: 'center'
            }}
          >
            {/* <Button
            onClick={(e) => handlePropertyDetailsClose(e)}
            variant="outlined"
            sx={{
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#333',
                backgroundColor: 'rgba(0,0,0,0.04)'
              },
              borderRadius: '4px'
            }}
          >
            Close
          </Button> */}
            {showViewMore && (
              <Button
                onClick={handleViewMore}
                variant="contained"
                sx={{
                  backgroundColor: '#d8b035',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 16px', sm: '10px 24px' },
                  '&:hover': {
                    backgroundColor: '#c5a02f'
                  },
                  borderRadius: '4px',
                  px: 3
                }}
              >
                View More Details
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Same Call Confirmation Dialog as Commercial Component */}
        {showCallConfirmation && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowCallConfirmation(false);
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: 0,
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                // animation: 'slideIn 0.3s ease-out',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  backgroundColor: '#d8b035',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px 12px 0 0',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, m: 0 }}>Confirm Call Request</Typography>
              </Box>
              <Box sx={{ padding: '25px', textAlign: 'center' }}>
                <Typography sx={{ mb: 2, color: '#333', lineHeight: 1.5 }}>
                  Would you like to make a direct call to <br /><strong>+91-7893092211</strong> via WhatsApp?
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '14px' }}>
                  This will open WhatsApp and initiate a call to our team.
                </Typography>
              </Box>
              <Box
                sx={{
                  padding: '0 25px 25px 25px',
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  sx={{
                    color: '#666',
                    borderColor: '#ddd',
                    textTransform: 'none',
                    flex: 1,
                    '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#ccc' }
                  }}
                  onClick={() => setShowCallConfirmation(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    backgroundColor: '#d8b035',
                    textTransform: 'none',
                    flex: 1,
                    '&:hover': { backgroundColor: '#c19b2e' }
                  }}
                  onClick={handleConfirmCall}
                >
                  Yes, Call
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Card>
    </>
  );
};

export default EnhancedPropertyCard;